import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthReply, Roles } from "./authTypes";

export interface AuthState {
  isAuthenticated: boolean;
  since: number | null;
  errorMessage: string | null;
  token: string | null;
  role: Roles;
  mainRole: Roles;
  // Shows the pages still in soft launch on top of what the role shows. A visual filter only:
  // the backend grants nothing for it. Like role and mainRole: isDeveloper is what the app shows
  // now, mainIsDeveloper what the account is, and developerHidden whether a developer has switched
  // the soft-launch pages off to see the app as everyone else does.
  isDeveloper: boolean;
  mainIsDeveloper: boolean;
  developerHidden: boolean;
  originalPath: string;
  displayName: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  since: Date.now(),
  errorMessage: "",
  token: "",
  role: "User",
  mainRole: "User",
  isDeveloper: false,
  mainIsDeveloper: false,
  developerHidden: false,
  originalPath: "/",
  displayName: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<AuthReply>) => {
      state.isAuthenticated = action.payload.isLogged;
      state.token = action.payload.token;
      state.role = action.payload.role;
      state.mainRole = action.payload.mainRole;
      state.mainIsDeveloper = action.payload.isDeveloper;
      state.isDeveloper = action.payload.isDeveloper && !state.developerHidden;
      state.since = Date.now();
      state.errorMessage = null;
      state.displayName = action.payload.displayName;
    },
    clearAuth: () => initialState,
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.errorMessage = action.payload;
    },
    clearErrorMessage: (state) => {
      state.errorMessage = null;
    },
    setOriginalPath: (state, action: PayloadAction<string>) => {
      state.originalPath = action.payload;
    },
    // A developer switches the soft-launch pages off, or back on.
    toggleDeveloperView: (state) => {
      state.developerHidden = !state.developerHidden;
      state.isDeveloper = state.mainIsDeveloper && !state.developerHidden;
    },
  },
});

export const {
  setAuth,
  clearAuth,
  setErrorMessage,
  clearErrorMessage,
  setOriginalPath,
  toggleDeveloperView,
} = authSlice.actions;
export default authSlice.reducer;
